/**
 * Version-poll — TDD-v2 §4.3 + plan-v2 §2.7 + gate 6.
 *
 * Polls `/version.json` every 5 minutes; on `commit` diff, surfaces
 * a "reload to update" banner in the SPA. Replaces Electron's four
 * auto-updater channels (`updater:check` / `install` / `pending` /
 * `downloaded` push) with a single browser-native mechanism.
 *
 * The SPA boots with the initial manifest baked in via Vite's
 * `__BUILD_VERSION__` define (or pulled from the loaded
 * `/version.json` on first poll if the define is absent). Each
 * subsequent poll compares `manifest.commit` to the baseline; if
 * different, the banner shows.
 *
 * Local-dev manifest (`commit: 'local-dev'`) suppresses the banner —
 * the developer's `pnpm dev` workflow doesn't trigger update churn.
 *
 * The hook (`useVersionPoll`) returns
 * `{ updateAvailable: boolean, newVersion?: string }`. The banner
 * is rendered by the App root (or by main.tsx for BG-4's
 * placeholder shell); user clicks reload → `window.location.reload()`.
 */

import { useEffect, useRef, useState } from 'react';

export interface VersionManifest {
  commit: string;
  buildTime: string;
  semver: string;
  channel: string;
}

export interface VersionPollState {
  /** True when the polled manifest differs from the baseline. */
  updateAvailable: boolean;
  /** Last successfully fetched manifest commit, when available. */
  latestCommit?: string;
}

export interface VersionPollDependencies {
  /** GET `/version.json`. Real impl is `window.fetch`. */
  fetchManifest: () => Promise<VersionManifest | null>;
  /** Poll cadence in milliseconds. Default 5 minutes per plan-v2 gate 6. */
  intervalMs: number;
  /** Initial manifest the SPA booted with. Comes from
   *  `import.meta.env.VITE_BUILD_COMMIT` + sibling vars. */
  baseline: VersionManifest;
  /**
   * Whether updates should be surfaced for the configured channel.
   * Default behaviour: suppress for `commit === 'local-dev'`. Tests
   * inject a real channel value.
   */
  shouldSurface?: (baseline: VersionManifest) => boolean;
}

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

export function defaultShouldSurface(baseline: VersionManifest): boolean {
  // Local-dev manifests never surface updates — the developer
  // doesn't want banner churn during `pnpm dev`.
  if (baseline.commit === 'local-dev') return false;
  // Builds with no commit (someone wrote an empty manifest) also
  // suppress — we can't compare without a baseline.
  if (!baseline.commit) return false;
  return true;
}

/**
 * Fetches `/version.json`. Returns `null` on error so the poll
 * loop can swallow transient network blips without surfacing a
 * banner (a one-shot 404 from a CloudFront cache miss shouldn't
 * tell the user to reload).
 */
export async function fetchVersionManifest(
  fetchImpl: typeof fetch = fetch,
): Promise<VersionManifest | null> {
  try {
    const resp = await fetchImpl('/version.json', { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = (await resp.json()) as Partial<VersionManifest>;
    if (typeof data.commit !== 'string' || !data.commit) return null;
    return {
      commit: data.commit,
      buildTime: data.buildTime ?? '',
      semver: data.semver ?? '',
      channel: data.channel ?? '',
    };
  } catch {
    return null;
  }
}

/**
 * React hook that drives the poll. Mounts a `setInterval`, fires
 * one immediate poll on mount, cancels on unmount. Returns the
 * current poll state.
 *
 * Stability invariant (codex round-1 O1): the `deps` object is often
 * a fresh literal on every render (e.g. `useVersionPoll(defaultVersionPollDependencies())`
 * in main.tsx). To avoid tearing down + re-creating the interval on
 * every parent re-render, the effect dependency array is FLAT —
 * only the primitive baseline commit + interval cadence trigger
 * re-subscription. `fetchManifest` and `shouldSurface` are held in
 * refs that update on every render, so callers swapping them mid-
 * flight still take effect on the next tick, but the interval
 * itself stays put.
 */
export function useVersionPoll(
  deps: VersionPollDependencies,
): VersionPollState {
  const [state, setState] = useState<VersionPollState>({
    updateAvailable: false,
  });

  const fetchRef = useRef(deps.fetchManifest);
  fetchRef.current = deps.fetchManifest;
  const shouldSurfaceRef = useRef(deps.shouldSurface ?? defaultShouldSurface);
  shouldSurfaceRef.current = deps.shouldSurface ?? defaultShouldSurface;
  const baselineRef = useRef(deps.baseline);
  baselineRef.current = deps.baseline;

  const baselineCommit = deps.baseline.commit;
  const intervalMs = deps.intervalMs;

  useEffect(() => {
    if (!shouldSurfaceRef.current(baselineRef.current)) return;

    let cancelled = false;
    async function tick(): Promise<void> {
      const latest = await fetchRef.current();
      if (cancelled || !latest) return;
      if (latest.commit === baselineCommit) {
        // Same build — clear any stale banner state (the user may
        // have reloaded after dismissing).
        setState({ updateAvailable: false, latestCommit: latest.commit });
        return;
      }
      setState({ updateAvailable: true, latestCommit: latest.commit });
    }

    // Fire once on mount; then on the interval.
    void tick();
    const interval = setInterval(() => void tick(), intervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [baselineCommit, intervalMs]);

  return state;
}

/**
 * Default dependencies — real browser globals + production defaults.
 * The bootstrap reads `import.meta.env` for the baseline manifest.
 *
 * Cached at module scope (codex round-1 O1 belt-and-braces): the
 * call site in main.tsx invokes this on every render, so handing
 * back a stable reference protects callers that don't flatten their
 * own dep arrays from re-subscription storms.
 */
let cachedDefaults: VersionPollDependencies | null = null;

/** Reset the module-scope cache. Test-only — keeps each test
 *  isolated from sibling tests that mutate `import.meta.env`. */
export function __resetDefaultVersionPollDependenciesForTests(): void {
  cachedDefaults = null;
}

export function defaultVersionPollDependencies(): VersionPollDependencies {
  if (cachedDefaults) return cachedDefaults;
  const env = (
    import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }
  ).env ?? {};
  cachedDefaults = {
    fetchManifest: () => fetchVersionManifest(),
    intervalMs: DEFAULT_INTERVAL_MS,
    baseline: {
      commit: env.VITE_BUILD_COMMIT || 'local-dev',
      buildTime: env.VITE_BUILD_TIME || '',
      semver: env.VITE_BUILD_SEMVER || '0.0.0',
      channel: env.VITE_BUILD_CHANNEL || 'dev',
    },
  };
  return cachedDefaults;
}
